import React from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import Navbar from "../../../globals/components/Navbar";
import {
  setLawFirmBondsPage,
} from "../../../store/slices/lawFirmBondsSlice";
import { setArchivesPage } from "../../../store/slices/bondArchivesSlice";
import BondCancellationDialog from "../../components/BondCancellationDialog";
import BondCantFindDialog from "../../components/BondCantFindDialog";
import BondFilterDialog from "../../components/BondFilterDialog";
import BondEndorsementDialog from "../../components/BondEndorsementDialog";
import PropTypes from "prop-types";
import { LAW_FIRM_BONDS_MODES } from "../helpers/lawFirmBondsPageConfig";
import useLawFirmBondsData from "../hooks/useLawFirmBondsData";
import LawFirmBondsTableBase from "./LawFirmBondsTableBase";

function LawFirmBondsStandardPage({ mode = LAW_FIRM_BONDS_MODES.APPROVE }) {
  const { meta, filters: fv, table: tv, dialogs: dv, constants: cv } = useLawFirmBondsData(mode);
  const { isTeamMember, pageConfig, dispatch } = meta;
  const {
    filterDialogOpen,
    setFilterDialogOpen,
    filters,
    setFilters,
    bondTypes,
    lawFirms,
    attorneys,
    professionalGuardians,
    bondNumberOptions,
    refreshToolbar,
    onPendingApprovalChip,
    applyFilters,
    clearFilters,
    availableSubcategories,
    availablePrincipals,
    availableEstates,
    dispatchArchiveFilters,
    principalFilterOptions,
    estateFilterOptions,
    filteredData,
    loading,
  } = fv;

  const {
    filteredRows,
    tableColumns,
    tableColSpan,
    pendingCount,
    noPendingBonds,
    archiveBondNumberOptions,
    handleSort,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    orderBy,
    order,
  } = tv;

  const {
    cancelDialogOpen,
    setCancelDialogOpen,
    selectedBondToCancel,
    setSelectedBondToCancel,
    cancellationFile,
    setCancellationFile,
    isCancelling,
    handleCancelBondSubmit,
    deleteDialogOpen,
    handleDeleteClose,
    handleDeleteConfirm,
    handleReviewBond,
    isDeleting,
    endorsementDialogOpen,
    setEndorsementDialogOpen,
    selectedBondToEndorse,
    setSelectedBondToEndorse,
    endorsementType,
    setEndorsementType,
    newBondAmount,
    setNewBondAmount,
    endorsementFile,
    setEndorsementFile,
    isSubmittingEndorsement,
    role,
    contactName,
    setContactName,
    contactEmail,
    setContactEmail,
    updatedName,
    setUpdatedName,
    counties,
    endorsementToCounty,
    setEndorsementToCounty,
    loadingCounties,
    estimatedAssets,
    setEstimatedAssets,
    dwbFirstName,
    setDwbFirstName,
    dwbMiddleName,
    setDwbMiddleName,
    dwbLastName,
    setDwbLastName,
    dwbSuffix,
    setDwbSuffix,
    dwbAlias,
    setDwbAlias,
    cantFindDialogOpen,
    setCantFindDialogOpen,
    cfBondNumber,
    setCfBondNumber,
    cfBondType,
    setCfBondType,
    cfName,
    setCfName,
    cfPrincipalName,
    setCfPrincipalName,
    cfAction,
    setCfAction,
    cfOtherInfo,
    setCfOtherInfo,
    cfSubmitting,
    nameChangeOk,
    internalReleaseDisabled,
    lawFirmEndorseSubmitDisabled,
    handleCantFindBondSubmit,
    handleSubmitEndorsement,
    handleReleaseRiderEndorsement,
  } = dv;

  const {
    getCancellationInstructionsList,
    lawFirmStatusOptions,
    archiveStatusOptions,
    capitalizeFirst,
  } = cv;

  return (
    <>
      <Navbar />
      <Box sx={{ padding: "20px" }}>
        <LawFirmBondsTableBase
          isTeamMember={isTeamMember}
          pageConfig={pageConfig}
          loading={loading}
          pendingCount={pendingCount}
          noPendingBonds={noPendingBonds}
          onPendingApprovalChip={onPendingApprovalChip}
          onRefresh={refreshToolbar}
          onOpenFilters={() => setFilterDialogOpen(true)}
          tableColumns={tableColumns}
          tableColSpan={tableColSpan}
          filteredRows={filteredRows}
          orderBy={orderBy}
          order={order}
          onSort={handleSort}
          onViewBond={(bond) => {
            const type = bond.bond_type.toLowerCase().replace(" ", "-");
            window.open(`/vermost-${type}-bond-update/${bond.bond_id}`, "_blank");
          }}
          onOpenCancel={(bond) => {
            setSelectedBondToCancel(bond);
            setCancellationFile(null);
            setCancelDialogOpen(true);
          }}
          onOpenEndorse={(bond) => {
            setSelectedBondToEndorse(bond);
            setEndorsementType("Increase");
            setNewBondAmount("");
            setEndorsementFile(null);
            setUpdatedName("");
            setEndorsementToCounty("");
            setEstimatedAssets("");
            setDwbFirstName("");
            setDwbMiddleName("");
            setDwbLastName("");
            setDwbSuffix("");
            setDwbAlias("");
            setEndorsementDialogOpen(true);
          }}
          capitalizeFirst={capitalizeFirst}
          filteredDataLength={filteredData.length}
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          onPageChange={(p) => {
            setPage(p);
            if (isTeamMember) {
              dispatch(setArchivesPage(p));
            } else {
              dispatch(setLawFirmBondsPage(p));
            }
          }}
          onRowsPerPageChange={(value) => {
            setRowsPerPage(value);
            setPage(0);
            if (!isTeamMember) {
              dispatch(setLawFirmBondsPage(0));
            }
          }}
          onCantFindBond={() => setCantFindDialogOpen(true)}
        />

        {!isTeamMember && (
          <BondCancellationDialog
            open={cancelDialogOpen}
            onClose={() => {
              if (!isCancelling) setCancelDialogOpen(false);
            }}
            bond={selectedBondToCancel}
            cancellationFile={cancellationFile}
            onFileChange={setCancellationFile}
            isCancelling={isCancelling}
            getCancellationInstructionsList={getCancellationInstructionsList}
            onSubmit={handleCancelBondSubmit}
          />
        )}

        {!isTeamMember && (
          <BondCantFindDialog
            open={cantFindDialogOpen}
            onClose={() => {
              if (!cfSubmitting) setCantFindDialogOpen(false);
            }}
            cfSubmitting={cfSubmitting}
            cfBondNumber={cfBondNumber}
            setCfBondNumber={setCfBondNumber}
            cfBondType={cfBondType}
            setCfBondType={setCfBondType}
            cfName={cfName}
            setCfName={setCfName}
            cfPrincipalName={cfPrincipalName}
            setCfPrincipalName={setCfPrincipalName}
            cfAction={cfAction}
            setCfAction={setCfAction}
            cfOtherInfo={cfOtherInfo}
            setCfOtherInfo={setCfOtherInfo}
            bondTypes={bondTypes}
            onSubmit={handleCantFindBondSubmit}
          />
        )}

        <BondFilterDialog
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          variant={isTeamMember ? "archive" : "lawFirm"}
          filters={filters}
          onArchiveFiltersChange={dispatchArchiveFilters}
          onLawFirmFiltersChange={setFilters}
          bondTypes={bondTypes}
          availableSubcategories={availableSubcategories}
          bondNumberOptionsArchive={archiveBondNumberOptions}
          bondNumberOptionsLawFirm={bondNumberOptions}
          availablePrincipals={availablePrincipals}
          availableEstates={availableEstates}
          principalFilterOptions={principalFilterOptions}
          estateFilterOptions={estateFilterOptions}
          lawFirms={lawFirms}
          attorneys={attorneys}
          professionalGuardians={professionalGuardians}
          lawFirmStatusOptions={lawFirmStatusOptions}
          archiveStatusOptions={archiveStatusOptions}
          capitalizeFirst={capitalizeFirst}
          clearFilters={clearFilters}
          applyFilters={() => applyFilters()}
        />

        {!isTeamMember && (
          <BondEndorsementDialog
            variant="lawFirm"
            open={endorsementDialogOpen}
            onClose={() => {
              if (!isSubmittingEndorsement) setEndorsementDialogOpen(false);
            }}
            selectedBondToEndorse={selectedBondToEndorse}
            endorsementType={endorsementType}
            setEndorsementType={setEndorsementType}
            newBondAmount={newBondAmount}
            setNewBondAmount={setNewBondAmount}
            endorsementFile={endorsementFile}
            setEndorsementFile={setEndorsementFile}
            isSubmittingEndorsement={isSubmittingEndorsement}
            contactName={contactName}
            setContactName={setContactName}
            contactEmail={contactEmail}
            setContactEmail={setContactEmail}
            updatedName={updatedName}
            setUpdatedName={setUpdatedName}
            counties={counties}
            endorsementToCounty={endorsementToCounty}
            setEndorsementToCounty={setEndorsementToCounty}
            loadingCounties={loadingCounties}
            estimatedAssets={estimatedAssets}
            setEstimatedAssets={setEstimatedAssets}
            dwbFirstName={dwbFirstName}
            setDwbFirstName={setDwbFirstName}
            dwbMiddleName={dwbMiddleName}
            setDwbMiddleName={setDwbMiddleName}
            dwbLastName={dwbLastName}
            setDwbLastName={setDwbLastName}
            dwbSuffix={dwbSuffix}
            setDwbSuffix={setDwbSuffix}
            dwbAlias={dwbAlias}
            setDwbAlias={setDwbAlias}
            role={role}
            nameChangeOk={nameChangeOk}
            internalReleaseDisabled={internalReleaseDisabled}
            lawFirmEndorseSubmitDisabled={lawFirmEndorseSubmitDisabled}
            onReleaseRider={() => void handleReleaseRiderEndorsement()}
            onSubmitEndorsement={() => void handleSubmitEndorsement()}
          />
        )}

        {isTeamMember && (
          <Dialog
            open={deleteDialogOpen}
            onClose={() => !isDeleting && handleDeleteClose()}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Delete Bond</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to delete this bond? This action cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleReviewBond} color="primary">
                Review Bond
              </Button>
              <Button onClick={handleDeleteClose} color="primary">
                Cancel
              </Button>
              <Button
                onClick={() => void handleDeleteConfirm()}
                color="error"
                variant="contained"
                disabled={isDeleting}
                startIcon={isDeleting && <CircularProgress size={20} />}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Box>
    </>
  );
}

LawFirmBondsStandardPage.propTypes = {
  mode: PropTypes.string,
};

export default LawFirmBondsStandardPage;
